import type { Mdg2EndpointModuleType } from "@fds/wm-typescript-poc-waap-stores-mdg2/src/abstracts";
import type { Mdg2StoreModuleEndpointOptionsInterface } from "@fds/wm-typescript-poc-waap-stores-mdg2/src/interfaces";
import {
  getLanguage,
  getEndpointOptions,
  baseMdg2EndpointModule,
  Mdg2EndpointRequestInterface,
  Mdg2EndpointStoreModuleFactory,
  NotationSearchByTextHighLevelRequestResultInterface,
  NotationSearchByTextRequestInterface,
  SearchSuggesterMdg2CriteriaInterface,
  SearchSuggesterStoreModuleDependencies,
  SearchSuggesterStoreModuleFactory,
  NotationSearchByTextSearchAttributes,
} from "@fds/wm-typescript-poc-waap-stores-mdg2";
import {
  AssetClass,
  SearchSuggesterInputsInterface,
} from "@fds/wm-typescript-poc-waap-library";

export class EgbSearchSuggesterStoreModuleFactory extends SearchSuggesterStoreModuleFactory {
  protected readonly egbAssetClasses = [
    "stock",
    "index",
    "currency",
    "interestRate",
    "future",
    "egbCurrency",
    "bond",
    "egbBond",
    "investmentProduct",
    "egbInvestmentProduct",
    "leveragedProduct",
    "egbLeveragedProduct",
    "egbInterestCapWarrant",
    "fund",
    "egbFund",
    "gold",
    "egbGold",
    "commodity",
  ];
  protected readonly egbAttributes = ["isin", "symbol", "longName"];
  protected readonly sortSearchResults = ["-matchScore"];
  protected readonly numberOfItemsPerAssetClass = 5;
  protected readonly searchTermMinimumCharactersRequired = 3;

  protected getNotationSearchByTextRequest(
    payload: SearchSuggesterInputsInterface<SearchSuggesterMdg2CriteriaInterface>,
    assetClass: AssetClass
  ) {
    payload.assetClasses = this.egbAssetClasses as AssetClass[];
    const customRequest = {
      data: {
        text: {
          value: payload.searchTerm,
          attributes: this
            .egbAttributes as NotationSearchByTextSearchAttributes[],
        },
        category: assetClass,
        portal: 2,
        validation: { onlyActive: true },
      },
      meta: {
        language: getLanguage(
          payload.language,
          this.defaultLanguage,
          this.options
        ),
        sort: this.sortSearchResults,
        pagination: { limit: this.numberOfItemsPerAssetClass },
      },
    };

    if (
      (assetClass as string) === "fund" ||
      (assetClass as string) === "egbFund"
    ) {
      (customRequest as any).data["fundIssuer"] = {
        exclude: { ids: [2164845, 1574010, 1574097] },
      };
    }

    return customRequest as any;
  }
  protected getGenericNotationSearchByTextRequest(
    payload: SearchSuggesterInputsInterface<SearchSuggesterMdg2CriteriaInterface>
  ) {
    const customRequest = {
      data: {
        text: {
          value: payload.searchTerm,
          attributes: this
            .egbAttributes as NotationSearchByTextSearchAttributes[],
        },
      },
      meta: {
        language: getLanguage(
          payload.language,
          this.defaultLanguage,
          this.options
        ),
        sort: this.sortSearchResults,
        pagination: {
          limit: this.numberOfItemsPerAssetClass,
        },
      },
    };
    return customRequest as any;
  }
  public getEndpoints(): SearchSuggesterStoreModuleDependencies {
    return {
      notationSearchByText: this.getModuleEntity(
        EgbNotationSearchByTextModuleFactory,
        getEndpointOptions(
          this.options?.endpointConfiguration,
          "notationSearchByText"
        )
      ),
    };
  }
  public async updateComponentsInputs(
    payload: SearchSuggesterInputsInterface<SearchSuggesterMdg2CriteriaInterface>
  ): Promise<boolean> {
    let isResponseSuccessful = true;

    if (
      !payload.searchTerm ||
      payload.searchTerm.length < this.searchTermMinimumCharactersRequired
    ) {
      return Promise.resolve(true);
    }

    try {
      if (payload.assetClasses.length > 0) {
        const nonGenericAssetClasses = this.getNonGenericAssetClasses(payload);

        await Promise.all(
          nonGenericAssetClasses.map(async (assetClass) =>
            this.updateNotationSearchByText(payload, assetClass)
          )
        );
      } else {
        await this.updateNotationSearchByText(payload);
      }
    } catch (e) {
      this.logger.error(e);
      isResponseSuccessful = false;
    }
    return isResponseSuccessful;
  }
}

export class EgbNotationSearchByTextModuleFactory extends Mdg2EndpointStoreModuleFactory<
  NotationSearchByTextRequestInterface,
  NotationSearchByTextHighLevelRequestResultInterface,
  Mdg2EndpointModuleType<NotationSearchByTextHighLevelRequestResultInterface>,
  Mdg2StoreModuleEndpointOptionsInterface
> {
  public static nameSpace = "egbNotationSearchByTextModule";

  public getModule(): Mdg2EndpointModuleType<NotationSearchByTextHighLevelRequestResultInterface> {
    const DEFAULT_VERSION = "v1";
    const DEFAULT_METHOD = "POST";
    const ROUTE = "/customer/notation/searchByText";

    const request: Mdg2EndpointRequestInterface = {
      method: this.options?.method ?? DEFAULT_METHOD,
      url: `/api/${this.options?.version ?? DEFAULT_VERSION}${ROUTE}`,
      ttl: this.options?.ttl,
    };

    return baseMdg2EndpointModule<
      NotationSearchByTextRequestInterface,
      NotationSearchByTextHighLevelRequestResultInterface
    >(request, this.mdg2Client, this.logger);
  }

  public getNameSpace(): string {
    return `${EgbNotationSearchByTextModuleFactory.nameSpace}_${JSON.stringify(
      this.options
    )}`;
  }
}
